import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import homeService from "../../features/home/services/home.service";
import { BlobContainerConsts } from "../../models/constants/blobContainerConsts";
import { BLOB_IMAGE_URL } from "../../config/appConfig";
import { MasterAffiliateTag } from "../../features/home/models";
import localization from "../../config/localization";
import { CMS_PAGE_MAP } from "../../models/constants/cmsPageMap";
import { CMSRetrieveUserResponse } from "../models/response/cmsRetrieveUserResponse";

function CuracaoBadge() {
    const sealScriptSrc =
        "https://54278f5c-9a34-4735-9204-ec1aa9abb7ad.snippet.anjouangaming.org/anj-seal.js";

    useEffect(() => {
        // In SPA, footer can mount after initial page load.
        // Injecting script on mount ensures ANJ seal initializes.
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = `${sealScriptSrc}?t=${Date.now()}`;
        script.async = true;
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, []);

    return (
        <div className="relative w-16 h-16 row desc-txt-area justify-content-center">
            <div
                id="anj-54278f5c-9a34-4735-9204-ec1aa9abb7ad"
                data-anj-seal-id="54278f5c-9a34-4735-9204-ec1aa9abb7ad"
                data-anj-image-size="128"
                data-anj-image-type="basic-small"
            />
        </div>
    );
}

const toSlug = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, "-")
        .replace(/[^a-z0-9-]/g, "");

const resolveCmsRouteKey = (page: CMSRetrieveUserResponse) => {
    const pageUrl = page.PageUrl ?? "";
    const pageName = page.PageName ?? "";

    const candidates = [
        pageUrl,
        pageName,
        pageUrl.replace(/\//g, ""),
        pageName.replace(/\//g, ""),
    ]
        .filter(Boolean)
        .map(toSlug);

    return (
        candidates.find((key) => CMS_PAGE_MAP[key]) ?? toSlug(pageUrl || pageName)
    );
};

export function Footer() {
    const [cmsUserPages, setCmsUserPages] = useState<CMSRetrieveUserResponse[]>([]);
    const [affiliateTags, setAffiliateTags] = useState<MasterAffiliateTag[]>([]);
    const pathsToHideFooter = ["sportsbookplus", "bharatsportsbook", "gamelaunch"];

    useEffect(() => {
        const loadFooterData = async () => {
            const [cmsResponse, affiliateResponse] = await Promise.all([
                homeService.fetchCmsUserList(),
                homeService.fetchMasterAffiliateTags(),
            ]);

            if (cmsResponse.isSuccess) {
                const rawDetails =
                    (cmsResponse.data as any)?.details ??
                    (cmsResponse.data as any)?.Details ??
                    [];
                setCmsUserPages(rawDetails);
            }

            if (affiliateResponse.isSuccess) {
                const rawDetails =
                    (affiliateResponse.data as any)?.details ??
                    (affiliateResponse.data as any)?.Details ??
                    [];
                setAffiliateTags(rawDetails);
            }
        };

        loadFooterData();
    }, []);

    const filteredCmsPages = useMemo(() => {
        return cmsUserPages.filter(
            (item: any) =>
                (item.Active ?? item.active) && (item.PageUrl ?? item.pageurl),
        );
    }, [cmsUserPages]);

    let showFooter = useMemo(() => {
        return !pathsToHideFooter.includes(window.location.pathname.split("/")[1].toLowerCase());
    }, [window.location.pathname]);


    if (!showFooter) {
        return null;
    }

    return (
    <footer
      id="footer-common"
      className={`mt-5 w-full ${!window.location.pathname.includes("exchange") ? "bg-black border-t border-[#464646] border-margin-top-md" : ""}`}
    >
      <div className="max-w-[79%] mx-auto px-4 pt-8 sm:pt-14 lg:pt-24 pb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5">
          <div className="md:col-span-3 xl:col-span-4">
            <nav>
              <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 justify-items-start">
                                {filteredCmsPages.map((item: any) => {
                                    const pageUrl = item.PageUrl ?? item.pageurl ?? "";
                                    const pageName = item.PageName ?? item.pagename ?? "";
                                    const routeKey = resolveCmsRouteKey({
                                        PageUrl: pageUrl,
                                        PageName: pageName,
                                    } as any);
                                    const titleKey = CMS_PAGE_MAP[routeKey]?.titleKey;
                                    const localizedLabel = titleKey
                                        ? localization.getString(titleKey)
                                        : "";
                                    const displayLabel =
                                        localizedLabel && localizedLabel !== titleKey
                                            ? localizedLabel
                                            : pageName || pageUrl;

                                    return (
                                        <li key={item.Id ?? item.id ?? pageUrl ?? pageName}>
                                            <Link
                                                to={`/${routeKey}`}
                        className="inline-block w-[160px] sm:w-auto text-[#A0A0A0] text-xs sm:text-sm no-underline hover:underline underline-offset-2 decoration-[#A0A0A0]/40 hover:text-white hover:decoration-white/60 transition-colors whitespace-normal break-words text-left"
                                            >
                                                {displayLabel}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>
          </div>

          <div className="flex flex-col items-center gap-4 col-span-1 md:col-span-3 xl:col-span-1 mt-3 xl:mt-0">
                            <CuracaoBadge />
                            <div className="flex items-center gap-3">
                                {affiliateTags.map((tag) => {
                                    return (
                                        <a
                                            key={tag.id ?? `${tag.name}-${tag.tagurl}`}
                                            href={tag.tagurl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={tag.name}
                                            className="block hover:opacity-80 transition-opacity"
                                        >
                                            <img
                                                src={
                                                    tag?.imageurl &&
                                                    `${BLOB_IMAGE_URL}${BlobContainerConsts.MasterAffiliateTags}/${tag.imageurl}`
                                                }
                                                alt={tag.name}
                                                loading="lazy"
                                                className="w-8 h-8 sm:w-9 sm:h-9 object-contain"
                                            />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                {/* License Text */}
                <p className="mt-6 sm:mt-10 text-[#707070] text-xs leading-relaxed text-center px-2">
                    {localization.footerpara2}
                </p>

                {/* Warning Text */}
                <p className="mt-4 sm:mt-6 text-[#707070] text-xs leading-relaxed text-center px-2">
                    {localization.footerpara3}
                </p>

                {/* Bottom Bar */}

                <div className="mt-6 flex flex-col sm:flex-row items-center sm:items-center justify-evenly text-xs">
                    {/* Left side */}
                    <span className="text-[#707070] mb-2 sm:mb-0">
                        {localization
                            .getString("footer_copyright")
                            .replace("{year}", new Date().getFullYear().toString())}
                    </span>

                    {/* Right side */}
                    <div className="flex items-center gap-6">
                        <Link
                            to="/privacy-policy"
                            className="text-[#A0A0A0] hover:text-white transition-colors"
                        >
                            {localization.getString("footer_privacy_policy")}
                        </Link>

                        <Link
                            to="/disclaimer"
                            className="text-[#A0A0A0] hover:text-white transition-colors"
                        >
                            {localization.getString("footer_disclaimer")}
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
